// src/modules/admin/components/ActivityEditModal/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useService } from '../../../../shared/hooks/useService';
import './styles.css';

const ACTIVITY_TYPES = [
    { value: 'running', label: 'Running' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'hiking', label: 'Hiking' },
    { value: 'walking', label: 'Walking' },
    { value: 'football', label: 'Football' },
    { value: 'badminton', label: 'Badminton' },
    { value: 'other', label: 'Other' },
];

const secondsToHms = (totalSeconds) => {
    const s = Math.round(totalSeconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const hmsToSeconds = (hms) => {
    const parts = hms.split(':');
    const h = parseInt(parts[0]) || 0;
    const m = parseInt(parts[1]) || 0;
    const s = parseInt(parts[2]) || 0;
    return h * 3600 + m * 60 + s;
};

const toLocalDateTimeString = (isoDate) => {
    const d = new Date(isoDate);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ActivityEditModal = ({ show, activity, onSave, onClose }) => {
    const service = useService();
    const [form, setForm] = useState({
        type: '',
        date: '',
        distance: '',
        duration: '00:00:00',
        elevation: '',
        calories: '',
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null);

    const resetForm = useCallback(() => {
        if (!activity) return;
        setForm({
            type: activity.activityType || '',
            date: activity.date ? toLocalDateTimeString(activity.date) : '',
            distance: activity.distance != null ? String(activity.distance) : '',
            duration: activity.duration != null ? secondsToHms(activity.duration) : '00:00:00',
            elevation: activity.elevation != null ? String(activity.elevation) : '',
            calories: activity.calories != null ? String(activity.calories) : '',
        });
        setSaveResult(null);
    }, [activity]);

    useEffect(() => {
        if (show && activity) {
            resetForm();
        }
    }, [show, activity, resetForm]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        setSaveResult(null);

        const durationSeconds = hmsToSeconds(form.duration);
        if (durationSeconds <= 0) {
            setSaveResult({ type: 'error', message: 'Duration must be greater than 0' });
            return;
        }

        const distance = parseFloat(form.distance);
        if (form.type !== 'other' && (isNaN(distance) || distance <= 0)) {
            setSaveResult({ type: 'error', message: 'Please enter a valid distance' });
            return;
        }

        const dateObj = new Date(form.date);
        if (isNaN(dateObj.getTime())) {
            setSaveResult({ type: 'error', message: 'Please enter a valid date' });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                type: form.type,
                date: dateObj.toISOString(),
                distance: isNaN(distance) ? 0 : distance,
                duration: durationSeconds,
                elevation: parseFloat(form.elevation) || 0,
                calories: parseFloat(form.calories) || 0,
                attachments: activity.attachments || [],
            };
            await service.updateActivity(activity.activityId, payload);
            setSaveResult({ type: 'success', message: 'Activity updated successfully' });
            onSave(activity.activityId, payload);
        } catch (err) {
            setSaveResult({ type: 'error', message: err.message || 'Failed to update activity' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        setSaveResult(null);
        onClose();
    };

    const displayName = activity
        ? (activity.firstName || activity.lastName
            ? `${activity.firstName || ''} ${activity.lastName || ''}`.trim()
            : activity.username || 'Unknown')
        : '';

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit Activity — {displayName}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {saveResult && (
                    <Alert
                        variant={saveResult.type === 'success' ? 'success' : 'danger'}
                        dismissible
                        onClose={() => setSaveResult(null)}
                        className="mb-3"
                    >
                        {saveResult.message}
                    </Alert>
                )}

                <Form>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Activity Type</Form.Label>
                                <Form.Select name="type" value={form.type} onChange={handleChange}>
                                    <option value="">Select type…</option>
                                    {ACTIVITY_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Date &amp; Time</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    name="date"
                                    value={form.date}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Distance (km)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="distance"
                                    value={form.distance}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Duration (HH:MM:SS)</Form.Label>
                                <Form.Control
                                    type="time"
                                    name="duration"
                                    value={form.duration}
                                    onChange={handleChange}
                                    step="1"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Elevation (m)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="elevation"
                                    value={form.elevation}
                                    onChange={handleChange}
                                    step="1"
                                    min="0"
                                    placeholder="0"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Calories (kcal)</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="calories"
                                    value={form.calories}
                                    onChange={handleChange}
                                    step="1"
                                    min="0"
                                    placeholder="0"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                            Saving…
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ActivityEditModal.propTypes = {
    show: PropTypes.bool.isRequired,
    activity: PropTypes.shape({
        activityId: PropTypes.string.isRequired,
        activityType: PropTypes.string,
        date: PropTypes.string,
        distance: PropTypes.number,
        duration: PropTypes.number,
        elevation: PropTypes.number,
        calories: PropTypes.number,
        attachments: PropTypes.array,
        username: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
    }),
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ActivityEditModal;
